import AsideMenu from "@/components/aside-menu"
import Header from "@/components/header"
import AIAssistantTrigger from "@/components/ai-assistant/AIAssistantTrigger"
import { useAuthStore } from "@/store/auth"
import { Layout, Row, ConfigProvider, theme as antdTheme } from "antd"
import React, { FC, Fragment, PropsWithChildren, useState, useCallback } from "react"
import { useTernaryDarkMode } from "usehooks-ts"
import { useMaestroContext } from "@/contexts/MaestroContext"
import { useMarketingCapture } from "@/marketingCapture/useMarketingCapture"
import "./ContentLayout.less"
import { useLocation } from "react-router-dom"

const ContentLayout: FC<{ title?: string; isHideAsideMenu?: boolean; wide?: boolean } & PropsWithChildren> = ({
  title,
  isHideAsideMenu,
  wide,
  children,
}) => {
  const userLoggedIn = useAuthStore(store => store.loggedIn)
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false);
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const { pageData } = useMaestroContext();
  const { allowed: captureMode } = useMarketingCapture();
  
  // Determine AI Assistant context based on current page
  const getAIContext = () => {
    if (location.pathname.startsWith('/integration') || location.pathname.startsWith('/details/')) {
      return 'integration-builder';
    }
    if (location.pathname.startsWith('/dashboard') || location.pathname === '/') {
      return 'dashboard';
    }
    return 'general';
  };
  
  const handleToggleCollapse = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);
  
  // Check if we're in a route that's already showing a sidebar through DashboardLayout
  const isDashboardRoute = location.pathname.startsWith('/dashboard') || 
                          location.pathname === '/' ||
                          location.pathname.startsWith('/details/') ||
                          location.pathname.startsWith('/integration') ||
                          location.pathname.startsWith('/start-integration') ||
                          location.pathname.startsWith('/edit-integration') ||
                          location.pathname.startsWith('/connector-library') ||
                          location.pathname.startsWith('/settings') ||
                          location.pathname.startsWith('/account') ||
                          location.pathname.startsWith('/faq') ||
                          location.pathname.startsWith('/help-center') ||
                          location.pathname.startsWith('/system-settings') ||
                          location.pathname.startsWith('/intelligence-insights') ||
                          location.pathname.startsWith('/intelligence-studio') ||
                          location.pathname.startsWith('/data-dimension-studio') ||
                          location.pathname.startsWith('/fleet') ||
                          location.pathname.startsWith('/automation-studio')

  return (
    <ConfigProvider theme={{ algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Layout className={`${isDarkMode ? "dark" : "light"} content-app-layout`}>
        {!isHideAsideMenu && userLoggedIn && !isDashboardRoute && (
          <Layout.Sider 
            width={250}
            className="main-sidebar"
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            breakpoint="lg"
            collapsedWidth={60}
            trigger={null}
          >
            <AsideMenu collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />
          </Layout.Sider>
        )}
        <Layout.Content className={"default-layout-content"}>
          <Fragment>
            <Row className={`content-layout${wide ? ' content-layout--wide' : ''}`}>
              {title && !captureMode && <Header title={title} />}
              {children}
            </Row>
          </Fragment>
        </Layout.Content>
        
        {/* AI Assistant - only when DashboardLayout is not already mounting it */}
        {userLoggedIn && !captureMode && !isDashboardRoute && (
          <AIAssistantTrigger 
            context={getAIContext()} 
            iconStyle="geometric"
            pageData={pageData || undefined}
          />
        )}
      </Layout>
    </ConfigProvider>
  )
}

export default ContentLayout
